import { useTranslation } from 'react-i18next';
import {
  ComboboxLabel,
  ComboboxTrigger,
  ComboboxPlaceholder,
  ComboboxButtonTrigger,
  ComboboxSelectedOption
} from '@anyui/core';
import { TextCutter } from '../../TextCutter';
import { useSelectContext } from '../select-context';
import { getOptionByValue, getOptionLabel } from '../utils';

interface SelectTriggerProps {
  onClear?: () => void;
  selectedValues: string[];
}

export const SelectTrigger = ({ onClear, selectedValues }: SelectTriggerProps) => {
  const { t } = useTranslation();
  const {
    selectProps: {
      disabled,
      multiple,
      classNames,
      triggerNode,
      placeholder,
      options = [],
      clearable = true,
      customSelectedLabel
    }
  } = useSelectContext();

  const hasSelectedValue = selectedValues.length > 0;
  const customPlaceholder = placeholder ?? t<string>('Selector.Please Select');

  const handleClear = () => {
    if (!clearable || disabled) return;

    onClear?.();
  };

  const renderSelectedLabel = () => {
    let label, prefixIcon;

    if (options.length) {
      if (multiple) {
        label =
          selectedValues.length > 2
            ? t('Selected', { count: selectedValues.length })
            : selectedValues
                .map((val) => {
                  const opt = getOptionByValue(options, val);

                  return opt ? getOptionLabel(opt) : val;
                })
                .join(', ');
      } else {
        const selectedOption = getOptionByValue(options, selectedValues[0]);
        prefixIcon = selectedOption?.prefixIcon;
        label = selectedOption ? getOptionLabel(selectedOption) : selectedValues[0];
      }
    } else {
      label = customPlaceholder;
    }

    if (customSelectedLabel) {
      label = customSelectedLabel(label, selectedValues);
    }

    return (
      <>
        {prefixIcon}
        <ComboboxLabel>
          <TextCutter text={label} lines={1} />
        </ComboboxLabel>
      </>
    );
  };

  return triggerNode ? (
    <ComboboxTrigger asChild>{triggerNode}</ComboboxTrigger>
  ) : (
    /* Should not use InputTrigger cause overlap with search function and design also support another input for searching */
    <ComboboxButtonTrigger
      size="md"
      onClear={handleClear}
      classNames={{ input: classNames?.trigger }}
      clearable={clearable && hasSelectedValue && !disabled}
    >
      {hasSelectedValue ? (
        <ComboboxSelectedOption>{renderSelectedLabel()}</ComboboxSelectedOption>
      ) : (
        <ComboboxPlaceholder>
          <TextCutter text={customPlaceholder} lines={1} />
        </ComboboxPlaceholder>
      )}
    </ComboboxButtonTrigger>
  );
};
